import { OASFormElement } from '@oas-ui/core'
import { pickOnColor as pickOnColorBase } from '../../shared/on-color.js'
import { lookupIcon } from '@oas-ui/icons/runtime'
import { isRtl } from '../../shared/direction.js'
import { normalizeSizeStrict, ALL_SIZES } from '../../shared/size.js'
import { devWarn } from '../../shared/dev-warn.js'

export type SwitchSize = 'xs' | 'small' | 'medium' | 'large' | 'xl'

/** status 校验态：success / warning / error（error 联动 aria-invalid） */
const VALID_STATUSES = ['error', 'warning', 'success'] as const
const warnedSizes = new Set<string>()

/** label for/button id 关联：确定性计数器（SSR 快照可重复，浏览器多实例不冲突） */
let switchIdCounter = 0

/** 非法 size 告警：回落 medium 并在 dev 下 console.warn 一次（同值去重）；sm/md/lg 别名由 shared/size 静默映射，不告警 */
function warnInvalidSize(raw: string): void {
  if (warnedSizes.has(raw)) return
  warnedSizes.add(raw)
  devWarn(`[oas-switch] 非法 size "${raw}"，已回落 medium；合法值：xs/small/medium/large/xl`)
}

/** 枚举归一化：合法值原样返回，空/非法值静默回落默认（非法值不告警，保持输出干净） */
function normalizeChoice(raw: string, fallback: string, valid: readonly string[]): string {
  if (raw === '') return fallback
  return valid.includes(raw) ? raw : fallback
}

/** 图标名是否可渲染（未注册名不渲染，与 tag 的 icon 通道一致） */
function isValidIcon(name: string): boolean {
  return name !== '' && lookupIcon(name) !== undefined
}

/**
 * 自定义色实心底的文字色：按相对亮度取深/浅，保证对比可读（共享实现见 `shared/on-color`）。
 */
function pickOnColor(color: string): string {
  return pickOnColorBase(color, { dark: '#18181b', light: '#ffffff', fallback: '' })
}

const STYLE = `
:host {
  display: inline-flex;
  align-items: center;
  gap: var(--oas-space-2);
  font-family: inherit;
  /* 热区一致性（视觉=热区）：宿主收缩到内容宽——竖向 flex 容器（如 oas-space direction="vertical"）
     里不再被 align-items: stretch 拉成整行宽而点击热区只有拨杆；
     点击热区扩展走 label 通道（label 属性/默认插槽渲染可点标签），不做整行空白可点 */
  width: fit-content;
  max-width: 100%;
}
:host([hidden]) {
  display: none;
}
button {
  /* 尺寸变量：medium 为默认值，.xs/.small/.large/.xl 覆盖；
     宿主可用 --oas-switch-width / --oas-switch-height / --oas-switch-thumb-size
     CSS 变量跨尺寸档覆盖轨道宽/高/滑块大小（WC 系共识的定制开口，不占属性 API） */
  --track-w: var(--oas-switch-width, 40px);
  --track-h: var(--oas-switch-height, 22px);
  --thumb-size: var(--oas-switch-thumb-size, 18px);
  --thumb-offset: 2px;
  --label-font: var(--oas-font-size-sm);
  appearance: none;
  border: none;
  padding: 0;
  cursor: pointer;
  position: relative;
  filter: var(--oas-glass-refraction, none);
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  min-width: var(--track-w);
  height: var(--track-h);
  border-radius: calc(var(--track-h) / 2);
  background: var(--oas-color-border);
  transition: background var(--oas-transition-base) var(--oas-ease-out);
}



button.xs {
  --track-w: var(--oas-switch-width, 22px);
  --track-h: var(--oas-switch-height, 12px);
  --thumb-size: var(--oas-switch-thumb-size, 8px);
  --label-font: var(--oas-font-size-xs);
}
button.small {
  --track-w: var(--oas-switch-width, 28px);
  --track-h: var(--oas-switch-height, 16px);
  --thumb-size: var(--oas-switch-thumb-size, 12px);
  --label-font: var(--oas-font-size-xs);
}
button.large {
  --track-w: var(--oas-switch-width, 52px);
  --track-h: var(--oas-switch-height, 28px);
  --thumb-size: var(--oas-switch-thumb-size, 24px);
  --label-font: var(--oas-font-size-md);
}
button.xl {
  --track-w: var(--oas-switch-width, 64px);
  --track-h: var(--oas-switch-height, 34px);
  --thumb-size: var(--oas-switch-thumb-size, 28px);
  --label-font: var(--oas-font-size-lg);
}
button[aria-checked='true'] {
  background: var(--oas-color-primary);
}
button:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
button[disabled] {
  cursor: not-allowed;
  opacity: 0.6;
  /* 禁用关折射（与 button/slider 同一纪律：禁用控件静态无装饰变形——含 busy 态，
     busy 同样 btn.disabled=true） */
  filter: none;
}
.thumb {
  position: absolute;
  top: var(--thumb-offset);
  left: var(--thumb-offset);
  width: var(--thumb-size);
  height: var(--thumb-size);
  border-radius: 50%;
  background: var(--oas-color-bg);
  box-shadow: var(--oas-shadow-sm);
  transition: left var(--oas-transition-base) var(--oas-ease-out);
}
button[aria-checked='true'] .thumb {
  /* 锚定右端而非固定位移：轨道带文案自动加宽时也能贴右端，不遮文案 */
  left: calc(100% - var(--thumb-size) - var(--thumb-offset));
}
/* RTL：书写方向镜像——滑块锚定侧反转（未开启贴视觉起点=右、开启贴视觉终点=左）。
   transform 不随 dir 翻转，故用 data-rtl 反向覆盖 left（过渡仍作用于 left 本身） */
:host([data-rtl]) button:not([aria-checked='true']) .thumb {
  left: calc(100% - var(--thumb-size) - var(--thumb-offset));
}
:host([data-rtl]) button[aria-checked='true'] .thumb {
  left: var(--thumb-offset);
}
/* 滑块图标：checked-icon / unchecked-icon 渲染 <oas-icon>，铺满滑块居中（尺寸跟随滑块） */
.thumb-icon {
  position: absolute;
  inset: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: calc(var(--thumb-size) * 0.6);
  color: var(--oas-color-primary);
  pointer-events: none;
}
.thumb-icon[hidden] {
  display: none;
}
/* 轨道内文案：显示在滑块对侧，nowrap 不换行、轨道随文案自动加宽（不溢出不变形） */
.label {
  font-size: var(--label-font);
  line-height: 1;
  white-space: nowrap;
  user-select: none;
  pointer-events: none;
  transition: color var(--oas-transition-base) var(--oas-ease-out);
}
.label[hidden] {
  display: none;
}
/* 单 flex 项（滑块绝对定位不参与 flex）：未开启文案靠 flex 终点、开启文案靠 flex 起点
   （justify-content 与 padding-inline-* 均为逻辑属性，RTL 下自动镜像） */
button:not([aria-checked='true']) {
  justify-content: flex-end;
}
button[aria-checked='true'] {
  justify-content: flex-start;
}
button:not([aria-checked='true']) .label {
  padding-inline-start: calc(var(--thumb-size) + var(--thumb-offset) * 2 + var(--oas-space-1));
  padding-inline-end: var(--oas-space-2);
  /* 轨道底为 --oas-color-border：text-secondary 压上感知分 58 不达标，用主文本色 */
  color: var(--oas-color-text-primary);
}
button[aria-checked='true'] .label {
  padding-inline-end: calc(var(--thumb-size) + var(--thumb-offset) * 2 + var(--oas-space-1));
  padding-inline-start: var(--oas-space-2);
  /* color 属性自定义轨道底时经 --oas-switch-on-color 按底色亮度取深/浅（JS 注入）；
     无自定义色回落 --oas-color-bg（primary token 深浅主题各自配对达标） */
  color: var(--oas-switch-on-color, var(--oas-color-bg));
}
/* 轨道外侧文案（size=xs/small 时展示） */
.outside-label {
  font-size: var(--oas-font-size-sm);
  line-height: 1;
  white-space: nowrap;
  user-select: none;
  color: var(--oas-color-text-secondary);
}
.outside-label[hidden] {
  display: none;
}
/* 外部标签（label 属性 / 默认插槽）：原生 label for 配对内部 button，
   点击标签即切换（热区承接通道），同时为 role=switch 提供可访问名称 */
.ext-label {
  order: 1;
  font-size: var(--oas-font-size-md);
  line-height: 1.4;
  color: var(--oas-color-text-primary);
  cursor: pointer;
  user-select: none;
}
.ext-label[hidden] {
  display: none;
}
:host([data-label-position='start']) .ext-label {
  order: -1;
}
/* 描述（description 属性 / slot[name=description]）：块级副文本渲染在标签下方，
   结构语义对齐 checkbox/radio 的 content > description */
.description {
  display: block;
  font-size: var(--oas-font-size-sm);
  line-height: 1.5;
  color: var(--oas-color-text-secondary);
}
.desc-text {
  display: block;
}
.desc-text[hidden],
.description[hidden] {
  display: none;
}
/* 卡片形态：描述落在 6% 主色淡底上——text-secondary 实测 4.44 边缘不达标（13px 常规字），
   卡片内改用 text-secondary-strong（更深）压过淡底；与 checkbox/radio 选择卡同口径 */
:host([data-variant='card']) .description {
  color: var(--oas-color-text-secondary-strong);
}
:host([disabled]) .ext-label,
:host([data-disabled]) .ext-label {
  cursor: not-allowed;
  color: var(--oas-color-text-disabled);
}
/* ---- 卡片形态（variant=card）：宿主盒即卡片——描边容器包住拨杆 + 标签 + 描述，
   整卡可点（宿主空白区点击委托切换）、选中描边着色（与 checkbox/radio 选择卡同视觉语言）。
   注意置于 checked 背景规则之后靠源顺序覆盖默认灰，status 系列再置于其后统一胜出 ---- */
:host([data-variant='card']) {
  padding: var(--oas-space-3) var(--oas-space-4);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-md);
  background: var(--oas-color-bg);
  cursor: pointer;
  transition: border-color var(--oas-transition-fast) var(--oas-ease-out),
    background var(--oas-transition-fast) var(--oas-ease-out);
}
:host([data-variant='card']:hover) {
  border-color: var(--oas-color-primary);
}
:host([data-variant='card'][checked]) {
  border-color: var(--oas-color-primary);
  background: color-mix(in srgb, var(--oas-color-primary) 6%, var(--oas-color-bg));
}
:host([data-variant='card'][disabled]),
:host([data-variant='card'][data-disabled]) {
  cursor: not-allowed;
  border-color: var(--oas-color-border);
  background: var(--oas-color-bg-disabled);
}
:host([data-variant='card'][data-status='success']) {
  border-color: var(--oas-color-success);
}
:host([data-variant='card'][data-status='warning']) {
  border-color: var(--oas-color-warning);
}
:host([data-variant='card'][data-status='error']),
:host([data-variant='card'][aria-invalid='true']) {
  border-color: var(--oas-color-danger);
}
/* ---- status 校验态：轨道随状态着色（覆盖默认灰/主色两态），焦点环同步染色 ----
   注意置于 checked 背景规则之后（同特异性下后者胜出需靠源顺序，status 优先） */
:host([data-status='success']) button {
  background: var(--oas-color-success);
}
:host([data-status='warning']) button {
  background: var(--oas-color-warning);
}
:host([data-status='error']) button,
:host([aria-invalid='true']) button {
  background: var(--oas-color-danger);
}
:host([data-status='success']) button:focus-visible {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--oas-color-success) 30%, transparent);
}
:host([data-status='warning']) button:focus-visible {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--oas-color-warning) 30%, transparent);
}
:host([data-status='error']) button:focus-visible,
:host([aria-invalid='true']) button:focus-visible {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--oas-color-danger) 30%, transparent);
}
/* 加载指示：放在滑块对侧（inset-inline-* 逻辑属性，RTL 下随滑块锚定侧自动镜像）、垂直居中
   （loading 时轨道内文案隐藏，互不遮挡） */
.spinner {
  position: absolute;
  top: 50%;
  width: 10px;
  height: 10px;
  margin-top: -7px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: oas-spin 0.8s linear infinite;
}
button:not([aria-checked='true']) .spinner {
  inset-inline-end: calc(var(--thumb-offset) + 2px);
  color: var(--oas-color-text-secondary);
}
button[aria-checked='true'] .spinner {
  inset-inline-start: calc(var(--thumb-offset) + 2px);
  color: var(--oas-color-bg);
}
@keyframes oas-spin {
  to {
    transform: rotate(360deg);
  }
}
`

export class OASSwitch extends OASFormElement {
  /** 原生表单集成（label for / FormData / reset / fieldset disabled）；沿静态原型链已可继承，显式声明便于阅读与检索 */
  static override formAssociated = true

  static override get observedAttributes(): string[] {
    return [
      'dir',
      'checked',
      'disabled',
      'loading',
      'checked-text',
      'unchecked-text',
      'size',
      'color',
      'disabled-skip',
      'label',
      'label-position',
      'checked-icon',
      'unchecked-icon',
      'true-value',
      'false-value',
      'required',
      'status',
      'aria-label',
      // 描述副文本 + 卡片形态（对齐 checkbox/radio 的 description/variant=card 三件一致性）
      'description',
      'variant',
    ]
  }

  private btn: HTMLButtonElement | null = null
  private btnId = ''
  /** 初始 checked 基线（form.reset 恢复目标）：初始渲染/受控写入跟随 checked 属性刷新 */
  private initialChecked = false
  /** 用户切换脏标记（对齐原生 dirty checkedness 语义）：置位后基线冻结，reset 恢复基线 */
  private checkedDirty = false
  /** before-change 异步在途：期间按钮禁用 + spinner（与 loading 同视觉），防重复触发 */
  private pending = false
  /** aria-invalid 由 status=error 设置的所有权标志（清理时只移除自己设置的，不动宿主自设值） */
  private invalidByStatus = false
  /** light DOM 默认插槽是否有实际内容（label slot 通道；MutationObserver 维护增量） */
  private slotHasContent = false
  private childObserver: MutationObserver | null = null

  /**
   * 切换前拦截钩子（JS property 通道，attribute 传不了函数，对齐 select 的 el.filterMethod 先例）：
   * `(next: boolean) => boolean | Promise<boolean>`，返回 false 或 Promise reject 则不切换；
   * 异步在途期间组件进入加载态（spinner + 禁点）。reject 视为否决（错误不重抛，
   * 避免 WC 事件链外产生未处理 rejection 噪音；宿主需要在钩子内部自行处理错误）。
   */
  beforeChange: ((next: boolean) => boolean | Promise<boolean>) | null = null

  /** 当前映射值：true-value/false-value 在场时为对应字符串，否则为布尔开关态 */
  get value(): string | boolean {
    return this.mappedValue(this.hasAttr('checked'))
  }
  set value(v: string | boolean) {
    const tv = this.getAttr('true-value', '')
    const fv = this.getAttr('false-value', '')
    if (tv !== '' && v === tv) this.toggleAttribute('checked', true)
    else if (fv !== '' && v === fv) this.toggleAttribute('checked', false)
    else if (tv === '' && fv === '') this.toggleAttribute('checked', v === true || v === 'true')
    // 未匹配映射值：忽略（不猜测语义）
  }

  /** 纯函数：SSR 快照与客户端渲染共用同一份模板，保证两路径结构严格一致 */
  private template(): string {
    return `
      <style>${STYLE}</style>
      <button part="switch" role="switch" aria-checked="false">
        <span class="thumb" part="thumb"><span class="thumb-icon" hidden></span></span>
        <span class="label" part="label" hidden></span>
        <span class="spinner" hidden></span>
      </button>
      <span class="outside-label" part="label" hidden></span>
      <label class="ext-label" part="label" hidden>
        <span class="label-text"></span>
        <span class="slot-wrap" hidden><slot></slot></span>
        <span class="description" part="description" hidden>
          <span class="desc-text" hidden></span><slot name="description"></slot>
        </span>
      </label>
    `
  }

  /** 缓存节点引用 + 绑定切换/焦点事件 + 分配确定性 id（render 与水合路径共用） */
  private bind(): void {
    this.btn = this.shadow.querySelector('button')
    this.btnId = `oas-switch-${++switchIdCounter}`
    if (this.btn) this.btn.id = this.btnId
    this.shadow.querySelector('.ext-label')?.setAttribute('for', this.btnId)

    this.btn?.addEventListener('click', () => this.requestToggle())
    // 焦点事件：内部 button 获得/失去焦点时派发 oas-focus / oas-blur
    this.btn?.addEventListener('focus', () => this.emit('focus'))
    this.btn?.addEventListener('blur', () => this.emit('blur'))

    // 块级拉伸热区：宿主被显式拉宽（竖向 oas-space align-stretch / 宿主 width:100%）时，
    // 点击落在宿主自身空白区（非 shadow 内 button/ext-label）的指针经委托切换——对齐
    // iOS 设置项整行点击语义；默认收缩形态（width: fit-content）下宿主无空白区，行为不变。
    // shadow 事件 retarget 后 target 恒为宿主，判定走 composedPath 首节点：
    // 首节点是宿主自身 = 点了空白区；首节点是 shadow 内元素 = 各自通道已处理，跳过防双触发
    this.addEventListener('click', (e) => this.onHostClick(e))

    // light DOM 默认插槽内容观察：label slot 通道内容增删/文本变化 → 重算标签可见性
    if (!this.childObserver) {
      const observer = new MutationObserver(() => {
        this.slotHasContent = this.computeSlotContent()
        this.update()
      })
      observer.observe(this, { childList: true, characterData: true, subtree: true })
      this.childObserver = observer
      this.onCleanup(() => {
        observer.disconnect()
        this.childObserver = null
      })
    }
    this.slotHasContent = this.computeSlotContent()
    // description 具名插槽内容增删 → 重算描述显隐（属性文本与 slot 分发并列互斥显示）
    this.shadow.querySelector('slot[name="description"]')?.addEventListener('slotchange', () => {
      this.update()
    })
  }

  protected override render(): void {
    this.shadow.innerHTML = this.template()
    this.bind()
    this.update()
  }

  /** 真水合：校验 SSR 快照结构（switch 按钮存在）后直接接管，跳过 shadow 重建 */
  protected override hydrate(): boolean {
    if (!this.shadow.querySelector('button[part="switch"]')) return false
    this.bind()
    return true
  }

  protected override update(): void {
    const btn = this.btn
    if (!btn) return
    const checked = this.hasAttr('checked')
    // form.reset 恢复基线：初始渲染/受控写入（宿主 setAttribute / value 赋值）跟随 checked 属性刷新；
    // 用户切换置脏后基线冻结（本组件切换会翻转 checked 属性，须与「宿主写入即新初始态」区分开）
    if (!this.checkedDirty) this.initialChecked = checked
    // disabled 就近读取全局禁用注入（组件显式 disabled > 豁免 > provider 注入 > 表单链路 fieldset）
    const disabled = this.injectDisabled()
    const busy = this.hasAttr('loading') || this.pending

    btn.setAttribute('aria-checked', String(checked))
    btn.disabled = disabled || busy
    // 原生表单数据 + 校验链同步（form-associated；无 name 浏览器自动不提交）。
    // 点击切换经 applyToggle → checked 属性 → attributeChangedCallback 汇入本方法，与受控写入同路径
    this.syncFormValue()
    this.syncValidity()

    // 尺寸：自身属性 > config-provider 注入 > medium（复用 button 的注入约定）；
    // 非法值回落 medium + dev warn（不再静默吞值）
    const sizeRaw = this.injectValue('size', 'medium')
    const { value: size, isValid: sizeValid } = normalizeSizeStrict(sizeRaw, ALL_SIZES, 'medium')
    if (!sizeValid) warnInvalidSize(sizeRaw)
    btn.className = size

    // 可访问名称便捷通道：宿主直设 aria-label 时镜像给内部 button（显式 aria 优先于 label 关联名）
    const ariaLabel = this.getAttribute('aria-label')
    if (ariaLabel != null && ariaLabel !== '') btn.setAttribute('aria-label', ariaLabel)
    else btn.removeAttribute('aria-label')

    // 开关文案：开启显示 checked-text，关闭显示 unchecked-text；
    // xs/small 尺寸时文案放轨道外侧（小轨道塞不下文字），其余尺寸显示在轨道内滑块对侧
    const text = checked ? this.getAttr('checked-text') : this.getAttr('unchecked-text')
    const showOutside = (size === 'xs' || size === 'small') && text !== ''
    const label = btn.querySelector<HTMLElement>('.label')
    const outside = this.shadow.querySelector<HTMLElement>('.outside-label')
    if (label) {
      label.hidden = showOutside || busy || text === ''
      label.textContent = showOutside ? '' : text
    }
    if (outside) {
      outside.hidden = !showOutside || busy
      outside.textContent = showOutside ? text : ''
    }
    this.classList.toggle('has-outside-label', showOutside && !busy)

    const spinner = btn.querySelector<HTMLElement>('.spinner')
    if (spinner) spinner.hidden = !busy

    // 滑块图标：开启显示 checked-icon、关闭显示 unchecked-icon（未设置/非法名隐藏；dataset 缓存增量换名）
    const thumbIcon = btn.querySelector<HTMLElement>('.thumb-icon')
    if (thumbIcon) {
      const name = checked ? this.getAttr('checked-icon', '') : this.getAttr('unchecked-icon', '')
      const valid = isValidIcon(name)
      thumbIcon.hidden = !valid
      if (valid) {
        if (thumbIcon.dataset.icon !== name) {
          thumbIcon.innerHTML = `<oas-icon name="${name}"></oas-icon>`
          thumbIcon.dataset.icon = name
        }
      } else {
        thumbIcon.innerHTML = ''
        delete thumbIcon.dataset.icon
      }
    }

    // 外部标签：默认插槽内容优先于 label 属性文本；description 在场时标签区承载描述副文本
    const ext = this.shadow.querySelector<HTMLElement>('.ext-label')
    const descVisible = this.syncDescription()
    if (ext) {
      const textSpan = ext.querySelector<HTMLElement>('.label-text')
      const slotWrap = ext.querySelector<HTMLElement>('.slot-wrap')
      const labelText = this.getAttr('label', '')
      ext.hidden = !(this.slotHasContent || labelText !== '' || descVisible)
      if (textSpan) {
        textSpan.hidden = this.slotHasContent
        textSpan.textContent = labelText
      }
      if (slotWrap) slotWrap.hidden = !this.slotHasContent
    }
    // label-position 镜像（start/end，非法值静默回落 end）
    this.setAttribute(
      'data-label-position',
      normalizeChoice(this.getAttr('label-position', ''), 'end', ['start', 'end']),
    )

    // 形态镜像（default/card，对齐 checkbox/radio 的 variant 契约）
    this.setAttribute('data-variant', normalizeChoice(this.getAttr('variant', ''), 'default', ['default', 'card']))

    // status 镜像 + aria-invalid 所有权（同 select 惯例；宿主自设 aria-invalid 等效 error 视觉）
    const status = normalizeChoice(this.getAttr('status', ''), '', VALID_STATUSES)
    if (status) this.setAttribute('data-status', status)
    else this.removeAttribute('data-status')
    if (status === 'error') {
      if (!this.hasAttribute('aria-invalid')) this.invalidByStatus = true
      this.setAttribute('aria-invalid', 'true')
    } else if (this.invalidByStatus) {
      this.invalidByStatus = false
      this.removeAttribute('aria-invalid')
    }

    // 注入禁用态镜像（:host([data-disabled]) 的 label 视觉联动钩子）
    this.toggleAttribute('data-disabled', disabled)

    // 书写方向镜像（data-rtl 供 :host([data-rtl]) 滑块锚定侧反向覆盖消费）
    this.toggleAttribute('data-rtl', isRtl(this))

    // 开启态自定义主色：以 --oas-color-primary 变量覆盖（焦点环等派生色一并生效）；
    // 轨道内 checked 文字色随自定义底色按亮度取深/浅（on-primary/bg 兜底对任意自定义色不保证可读）
    const color = this.getAttr('color')
    if (color) {
      btn.style.setProperty('--oas-color-primary', color)
      const on = pickOnColor(color)
      if (on) btn.style.setProperty('--oas-switch-on-color', on)
      else btn.style.removeProperty('--oas-switch-on-color')
    } else {
      btn.style.removeProperty('--oas-color-primary')
      btn.style.removeProperty('--oas-switch-on-color')
    }
  }

  /** label 点击聚焦委托：把焦点交给 shadow 内 button */
  override focus(options?: FocusOptions): void {
    this.btn?.focus(options)
  }
  override blur(): void {
    this.btn?.blur()
  }

  /** 默认插槽是否有实际内容（元素节点或非空白文本节点；空白文本不算） */
  private computeSlotContent(): boolean {
    const slot = this.shadow.querySelector('slot')
    const nodes = slot?.assignedNodes({ flatten: true }) ?? []
    return nodes.some(
      (n) => n.nodeType === Node.ELEMENT_NODE || (n.nodeType === Node.TEXT_NODE && (n.textContent ?? '').trim() !== ''),
    )
  }

  /**
   * 描述（description 属性 / slot[name=description]）显隐同步：返回是否可见（供 ext-label
   * 联动显隐）。约束与 checkbox 一致——组件永不写 slot 子树（防 slotchange 泵死循环），
   * 属性文本写独立的 .desc-text 节点，与 slot 分发并列互斥显示。
   */
  private syncDescription(): boolean {
    const descWrap = this.shadow.querySelector<HTMLElement>('.description')
    if (!descWrap) return false
    const slot = descWrap.querySelector<HTMLSlotElement>('slot[name="description"]')
    const hasSlotContent = (slot?.assignedElements().length ?? 0) > 0
    const descText = this.getAttr('description', '')
    const descTextEl = descWrap.querySelector<HTMLElement>('.desc-text')
    if (descTextEl) {
      if (descTextEl.textContent !== descText) descTextEl.textContent = descText
      descTextEl.hidden = hasSlotContent || descText === ''
    }
    descWrap.hidden = !hasSlotContent && descText === ''
    return !descWrap.hidden
  }

  /** true-value/false-value 映射：未设置为布尔，部分设置时缺失侧回落布尔 */
  private mappedValue(next: boolean): string | boolean {
    const tv = this.getAttr('true-value', '')
    const fv = this.getAttr('false-value', '')
    if (tv === '' && fv === '') return next
    if (next) return tv !== '' ? tv : true
    return fv !== '' ? fv : false
  }

  /**
   * 宿主自身空白区点击委托：块级拉伸（宿主宽于内容）时整行可点。
   * composedPath 首节点为宿主自身才委托——shadow 内 button/ext-label 的点击
   * 首节点是各自元素，由自身通道处理，跳过以避免双触发；拦截语义与 button 一致
   * （disabled/loading/before-change 统一走 requestToggle）。
   */
  private onHostClick(e: Event): void {
    if (e.composedPath()[0] !== this) return
    this.requestToggle()
  }

  /** 切换请求统一入口：disabled/loading/pending 拦截，before-change 钩子仲裁 */
  private requestToggle(): void {
    if (this.injectDisabled() || this.hasAttr('loading') || this.pending) return
    const next = !this.hasAttr('checked')
    const hook = this.beforeChange
    if (typeof hook !== 'function') {
      this.applyToggle(next)
      return
    }
    this.pending = true
    this.update()
    Promise.resolve()
      .then(() => hook(next))
      .then((ok) => {
        if (ok !== false) this.applyToggle(next)
      })
      .catch(() => {
        // reject = 否决（不切换 + 恢复可点），错误不重抛：宿主在钩子内自行处理
      })
      .finally(() => {
        this.pending = false
        this.update()
      })
  }

  private applyToggle(next: boolean): void {
    // 用户切换置脏：冻结 reset 基线（对齐原生「用户交互不改 defaultChecked」——本组件切换
    // 会翻转 checked 属性本身，故以脏标记区分用户态与宿主受控态）；表单数据经 checked 属性
    // → attributeChangedCallback → update() 自动重同步
    this.checkedDirty = true
    this.toggleAttribute('checked', next)
    this.emit('change', { checked: next, value: this.mappedValue(next) })
  }

  /** 表单值快照（照原生 checkbox「开才提交」语义）：开 → true-value 属性在场取其值，缺省 'on'；关 → null（false-value 不进 FormData，与原生一致） */
  protected override getFormValue(): string | null {
    if (!this.hasAttr('checked')) return null
    const tv = this.getAttr('true-value', '')
    return tv !== '' ? tv : 'on'
  }

  /** 原生校验链同步：required 且关 → valueMissing（switch 语义：开 = 有值） */
  private syncValidity(): void {
    if (this.hasAttr('required') && !this.hasAttr('checked')) {
      this.setValidity({ valueMissing: true }, this.t('form.valueMissing'))
    } else {
      this.setValidity({})
    }
  }

  /** 表单 reset：恢复 checked 初始基线（checked 属性初始态），不派发事件（与原生 reset 一致）；表单数据由基类回调统一重同步 */
  protected override resetFormValue(): void {
    this.checkedDirty = false
    this.toggleAttribute('checked', this.initialChecked)
  }

  /** shadow 内真实表单控件：本组件内层是 role=switch 的 button（非 input，无 tabindex 属性），基类默认选择器匹配不到，必须覆盖 */
  protected override get innerControl(): HTMLElement | null {
    return this.btn
  }
}
